import moment from "moment";
import Timeoff from "../models/Timeoff";
import OpsDateUtils from "./OpsDateUtils";
import { TimeOffStatus, TimeOffType } from "../types/TimeOffType";

export type CoverageState = "timeoff" | "weekend" | "working";

export interface CoverageRow {
  requesterExternalId: string;
  fullName: string;
  days: Set<string>;
}

const DAYS_IN_TWO_WEEKS = 14;

export const toKey = (date: Date): string => moment(date).format("YYYY-MM-DD");

export const buildTwoWeekRange = (from: Date = new Date()): Date[] => {
  const start = OpsDateUtils.setHoursToZero(new Date(from));
  return Array.from({ length: DAYS_IN_TWO_WEEKS }, (_, i) => {
    const day = new Date(start);
    day.setDate(start.getDate() + i);
    return OpsDateUtils.setHoursToZero(day);
  });
};

export const expandTimeOffDays = (timeoff: Timeoff): string[] => {
  const days: string[] = [];
  const start = OpsDateUtils.setHoursToZero(OpsDateUtils.utcOffset(new Date(timeoff.shortStartDate)));
  const end = OpsDateUtils.setHoursToZero(OpsDateUtils.utcOffset(new Date(timeoff.shortEndDate)));
  const cursor = new Date(start);
  while (cursor <= end) {
    days.push(toKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
};

const EXCLUDED_STATUSES = new Set<string>([TimeOffStatus.REJECTED, TimeOffStatus.CANCELLED]);
const EXCLUDED_TYPES = new Set<string>([TimeOffType.BIRTHDAY, TimeOffType.HOLIDAY]);

export const buildCoverageRows = (timeoffs: Timeoff[], range: Date[]): CoverageRow[] => {
  const visible = new Set(range.map(toKey));
  const map = new Map<string, CoverageRow>();

  for (const t of timeoffs) {
    if (EXCLUDED_STATUSES.has(t.status) || EXCLUDED_TYPES.has(t.type)) continue;

    const daysInRange = expandTimeOffDays(t).filter((d) => visible.has(d));
    if (daysInRange.length === 0) continue;

    const existing = map.get(t.requesterExternalId);
    if (existing) {
      daysInRange.forEach((d) => existing.days.add(d));
    } else {
      map.set(t.requesterExternalId, {
        requesterExternalId: t.requesterExternalId,
        fullName: t.requesterName || `${t.requester?.firstName ?? ""} ${t.requester?.lastName ?? ""}`.trim(),
        days: new Set(daysInRange),
      });
    }
  }

  return [...map.values()].sort((a, b) => a.fullName.localeCompare(b.fullName));
};

export const resolveCellState = (date: Date, days: Set<string>): CoverageState => {
  if (days.has(toKey(date))) return "timeoff";
  if (OpsDateUtils.isWeekend(date)) return "weekend";
  return "working";
};
